import BedDouble from 'lucide-react-native/icons/bed-double';
import { StyleSheet, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { HotelsMap, type WideHotelsMap } from '@/components/detail/hotels-map';
import { Button } from '@/components/ui/button';
import { Fonts, Radius, Spacing } from '@/constants/theme';
import { useTheme } from '@/hooks/use-theme';
import { cityStay, eventStay, STAY22_SOURCE, stay22Url, type Stay } from '@/lib/hotels';
import { localizedTitle } from '@todo/core/i18n/event-content';
import { formatDayMonth } from '@todo/core/i18n/format';
import type { Locale } from '@todo/core/i18n/locales';
import { useLocale } from '@todo/core/i18n/use-locale';
import { openExternal, type OutboundMeta } from '@todo/core/open-external';
import type { DbEvent } from '@todo/core/supabase';
import { SITE } from '@/site';

// "Where to stay": a hotel search around the venue or city via Stay22
// (src/lib/hotels.ts). The link is built on press, not baked into the HTML, so
// the dates can be settled against today and crawlers see no outbound URL.
// Under the button, on web, the same search as a map that opens on demand
// (HotelsMap).

/** A YYYY-MM-DD day as "24 Oct" — as a UTC noon, so no timezone can shift it. */
function formatDay(day: string, locale: Locale): string {
  return formatDayMonth(new Date(`${day}T12:00:00Z`), 'UTC', locale);
}

function HotelsButton({
  stay,
  campaign,
  meta,
  label,
  zoom,
  wideMap,
}: {
  stay: Stay;
  campaign: string;
  meta: Omit<OutboundMeta, 'kind' | 'source' | 'locale'>;
  label: string;
  /** Of the map under the button. */
  zoom: number;
  /** The event page's own map, for desktop (HotelsMap's `wide`). */
  wideMap?: WideHotelsMap;
}) {
  const theme = useTheme();
  const { locale } = useLocale();
  return (
    <View style={styles.buttonBlock}>
      <Button
        variant="outline"
        leadingIcon={<BedDouble size={18} color={theme.text} />}
        onPress={() =>
          openExternal(stay22Url(stay, { campaign, locale }), {
            kind: 'hotel',
            source: STAY22_SOURCE,
            locale,
            ...meta,
          })
        }>
        {label}
      </Button>
      <HotelsMap stay={stay} campaign={campaign} zoom={zoom} wide={wideMap} />
    </View>
  );
}

/**
 * Under the event page's ticket CTA: hotels near the venue for the night of
 * the event (the show's soonest date). The caller hides it once the event is
 * past. `wideMap`: on desktop this block sits in the narrow booking column,
 * so the page shows the map full width under its main card instead.
 */
export function EventHotels({ event, wideMap }: { event: DbEvent; wideMap?: WideHotelsMap }) {
  const theme = useTheme();
  const { t } = useTranslation();
  const { locale } = useLocale();
  const stay = eventStay(event);
  if (!stay) return null;

  const where = stay.atVenue && event.venue_name
    ? t('hotels.nearVenue', { venue: event.venue_name })
    : event.city_id
      ? t('hotels.inCity', { inCity: t(`boatRental.cities.${event.city_id}.inCity`) })
      : null;
  const dates =
    stay.checkin && stay.checkout
      ? t('hotels.dates', {
          checkin: formatDay(stay.checkin, locale),
          checkout: formatDay(stay.checkout, locale),
        })
      : null;
  const hint = [where, dates].filter(Boolean).join(' · ');

  return (
    <View style={[styles.eventBlock, { borderColor: theme.border }]}>
      <Text style={[styles.eventHeading, { color: theme.text }]}>{t('hotels.eventHeading')}</Text>
      {hint ? <Text style={[styles.hint, { color: theme.textSecondary }]}>{hint}</Text> : null}
      <HotelsButton
        stay={stay}
        campaign={`event_${event.city_id ?? SITE.stay22.region}`}
        meta={{ id: event.id, title: localizedTitle(event, locale), city: event.city_id }}
        label={t('hotels.cta')}
        zoom={14}
        wideMap={wideMap}
      />
    </View>
  );
}

/**
 * "Where to stay in Nice": a section for pages that know only the city — the
 * city guide and articles (their first city). No dates; the visitor picks
 * them on the booking site. Nothing for an unknown city.
 */
export function CityHotels({
  cityId,
  campaign,
  pageId,
}: {
  cityId: string | null | undefined;
  /** Which kind of page, for Stay22's reports: 'city' | 'article'. */
  campaign: string;
  /** Umami `id` of the click: the page it came from (city id, article _id). */
  pageId: string;
}) {
  const theme = useTheme();
  const { t } = useTranslation();
  const stay = cityStay(cityId);
  if (!stay || !cityId) return null;

  const inCity = t(`boatRental.cities.${cityId}.inCity`);
  return (
    <View style={[styles.card, { backgroundColor: theme.card, borderColor: theme.border }]}>
      <Text
        role="heading"
        aria-level={2}
        style={[styles.sectionHeading, { color: theme.text, fontFamily: Fonts?.serif }]}>
        {t('hotels.sectionHeading', { inCity })}
      </Text>
      <Text style={[styles.sectionText, { color: theme.textSecondary }]}>
        {t('hotels.sectionText', { inCity })}
      </Text>
      <HotelsButton
        stay={stay}
        campaign={`${campaign}_${cityId}`}
        meta={{ id: pageId, city: cityId }}
        label={t('hotels.ctaCity', { inCity })}
        zoom={13}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  eventBlock: {
    marginTop: Spacing.four,
    paddingTop: Spacing.three,
    borderTopWidth: StyleSheet.hairlineWidth,
    gap: Spacing.one,
  },
  eventHeading: {
    fontSize: 15,
    fontWeight: '700',
  },
  hint: {
    fontSize: 14,
    lineHeight: 20,
  },
  buttonBlock: {
    marginTop: Spacing.two,
  },
  card: {
    borderWidth: 1,
    borderRadius: Radius.lg,
    padding: Spacing.four,
    gap: Spacing.two,
  },
  sectionHeading: {
    fontSize: 22,
    fontWeight: '700',
  },
  sectionText: {
    fontSize: 15,
    lineHeight: 24,
  },
});
